/**
 * MobileDrawer — "More" navigation as a bottom sheet.
 * Surfaces the full navigation tree that doesn't fit in the 5-slot tab bar.
 *
 * Accepts optional `sections` to swap in a different nav set (e.g. Planning app).
 * When omitted it uses the default prioritization DRAWER_SECTIONS.
 */
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, LogOut } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { APPS_HREF, DRAWER_SECTIONS, SEARCH_SENTINEL, type MobileNavItem, type MobileNavSection } from './navConfig';
import { usePhoneMorePages } from './usePhoneMorePages';
import { ProductModeSwitcher } from '../components/prioritization/ProductModeSwitcher';
import { TenantSwitcher } from '../components/prioritization/TenantSwitcher';
import { SpaceSwitcher } from '../components/prioritization/SpaceSwitcher';
import { useUserStore } from '../stores/prioritization/userStore';
import { useAccessibleApps } from '../apps/RequireAppAccess';
import { clearTenantContext } from '../lib/tenantContext';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Override the default drawer sections (used by the Planning shell). */
  sections?: MobileNavSection[];
  /** Open command palette search. */
  onSearch?: () => void;
  /** Draw the Priorities ↔ Plans switch (default true). */
  showProductSwitch?: boolean;
  /**
   * List every other desktop sidebar page under "More pages" (the full menu;
   * not the role-based short menu, which already mirrors its desktop menu).
   */
  showMorePages?: boolean;
  /**
   * The workspace and space switches the matching desktop sidebar draws at its
   * foot: both (Priorities, Plans), the space only (Mandate), or none (the
   * role-based short menu, whose desktop sidebar has neither).
   */
  switchers?: 'workspace-and-space' | 'space' | 'none';
}

export function MobileDrawer({
  open,
  onClose,
  sections,
  onSearch,
  showProductSwitch = true,
  showMorePages = false,
  switchers = 'workspace-and-space',
}: MobileDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useUserStore((s) => s.signOut);
  const { apps: accessibleApps } = useAccessibleApps();

  // The Apps entry is declared statically in navConfig, but whether a user has
  // any apps is resolved at runtime (module + flag + entitlement). Drop it when
  // there are none, so the drawer matches the desktop sidebar instead of
  // offering a link that lands on an empty launcher.
  const navSections = useMemo(() => {
    const base = sections ?? DRAWER_SECTIONS;
    if (accessibleApps.length > 0) return base;
    return base
      .map((s) => ({ ...s, items: s.items.filter((i) => i.href !== APPS_HREF) }))
      .filter((s) => s.items.length > 0);
  }, [sections, accessibleApps.length]);

  const go = (href: string) => {
    onClose();
    if (href === SEARCH_SENTINEL) {
      onSearch?.();
      return;
    }
    if (location.pathname !== href) navigate(href);
  };

  const handleSignOut = () => {
    onClose();
    clearTenantContext();
    signOut();
    navigate('/auth/login', { replace: true });
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Navigate">
      <div className="pa-stagger space-y-5">
        {showProductSwitch && (
          <div className="px-1">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[var(--text-ter)]">
              Product
            </p>
            <ProductModeSwitcher onNavigate={onClose} className="border-b-0 px-0 pt-0 pb-0" />
          </div>
        )}

        {navSections.map((section) => (
          <div key={section.label}>
            <p className={`px-1 mb-2 ${sectionLabelClass}`}>
              {section.label}
            </p>
            <DrawerItemList items={section.items} onGo={go} />
          </div>
        ))}

        {showMorePages && <MorePages onGo={go} />}

        {/* At the foot, as on the desktop sidebar: both lists open upward.
            Each switch draws nothing when there is nothing to switch to. */}
        {switchers !== 'none' && (
          <div className="space-y-2 empty:hidden">
            {switchers === 'workspace-and-space' && <TenantSwitcher compact />}
            <SpaceSwitcher />
          </div>
        )}

        <button
          type="button"
          onClick={handleSignOut}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-[var(--rule)] text-gray-600 dark:text-[var(--text-sec)] active:bg-gray-50 dark:active:bg-[var(--hover-tint)]"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-[15px]">Sign out</span>
        </button>
      </div>
    </BottomSheet>
  );
}

const sectionLabelClass =
  'text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[var(--text-ter)]';

function DrawerItemList({
  items,
  onGo,
  framed = true,
}: {
  items: MobileNavItem[];
  onGo: (href: string) => void;
  /** Draw the rounded border (off inside a group that already has one). */
  framed?: boolean;
}) {
  return (
    <div
      className={`${framed ? 'rounded-xl border border-gray-200 dark:border-[var(--rule)] ' : ''}overflow-hidden divide-y divide-gray-100 dark:divide-[var(--rule)]`}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.href + item.label}
            type="button"
            onClick={() => onGo(item.href)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-50 dark:active:bg-[var(--hover-tint)]"
          >
            <span className="h-9 w-9 flex-shrink-0 rounded-lg bg-primary-50 dark:bg-[var(--active-tint)] flex items-center justify-center text-primary-700 dark:text-[var(--text-pri)]">
              <Icon className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1 text-[15px] text-gray-800 dark:text-[var(--text-sec)]">{item.label}</span>
            <ChevronRight className="h-4 w-4 text-gray-300 dark:text-[var(--text-ter)]" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Every other page the desktop sidebar offers (usePhoneMorePages), one folded
 * group per sidebar section. Mounted only while the drawer is open, so the
 * desktop menu's lookups run when someone looks, not on every phone page.
 */
function MorePages({ onGo }: { onGo: (href: string) => void }) {
  const groups = usePhoneMorePages();
  if (groups.length === 0) return null;
  return (
    <div>
      <p className={`px-1 mb-2 ${sectionLabelClass}`}>More pages</p>
      <div className="space-y-2">
        {groups.map((group) => (
          <details
            key={group.label}
            className="group rounded-xl border border-gray-200 dark:border-[var(--rule)] overflow-hidden"
          >
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-[15px] text-gray-800 dark:text-[var(--text-sec)] [&::-webkit-details-marker]:hidden">
              <span className="flex-1">{group.label}</span>
              <span className="text-xs tabular-nums text-gray-400 dark:text-[var(--text-ter)]">{group.items.length}</span>
              <ChevronDown className="h-4 w-4 text-gray-300 transition-transform group-open:rotate-180 dark:text-[var(--text-ter)]" />
            </summary>
            <div className="border-t border-gray-100 dark:border-[var(--rule)]">
              <DrawerItemList items={group.items} onGo={onGo} framed={false} />
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
